<template>
    <div class="popup-frame edit-import-template-popup">
        <div @click="closePopup()" class="close-button"></div>
        <div class="popup-title-wrapper"><h1>
            {{templateId
            ? t('edit_import_template_popup.edit_title')
            : t('edit_import_template_popup.new_title')}}
        </h1></div>
        <table class="edit-table">
            <tbody>
            <tr>
                <td><label for="template_popup_name">{{t('edit_import_template_popup.name')}}</label></td>
                <td><input v-model="templateName" type="text" data-automation-id="template-name" id="template_popup_name"></td>
            </tr>
            <tr>
                <td><label for="template_import_file">{{t('edit_import_template_popup.load_file_label')}}</label></td>
                <td><input id="template_import_file" type="text" disabled="disabled" class="ltr"
                           :value="selectedFileName"
                ></td>
                <td>
                    <button @click="$refs.file.click()" class="neutral-button min-full-width" :class="loadingFile ? 'button-loading': ''">{{t('file_controls.file_choose.m')}}</button>
                    <input v-on:change="fileChange" ref="file" type="file" accept=".csv"
                           style="display: block; font-size: 0; visibility: hidden;"></td>
            </tr>
            </tbody>
        </table>
        <div class="main-popup-content">
            <table class="subitems-table">
                <thead>
                <tr>
                    <th style="width:25%">{{t('edit_import_template_popup.label')}}</th>
                    <th style="width:25%">{{t('edit_import_template_popup.sample_value')}}</th>
                    <th style="width:25%">{{t('edit_import_template_popup.field')}}</th>
                    <th style="width:25%">{{t('edit_import_template_popup.format')}}</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr data-automation-id="field" v-for="(field, index) in fields">
                    <td><input v-model="field.label" type="text" data-name="template-row-label"></td>
                    <td><input :value="field.sampleValue" type="text" disabled data-name="template-row-sample-value"></td>
                    <td><select v-model="field.field" data-name="template-row-field">
                        <option value=""></option>
                        <option
                                v-for="option in fieldsList"
                                :value="option.value">
                            {{option.text}}
                        </option>
                    </select></td>

                    <td><select
                            v-model="field.format"
                            :style="dateFields.includes(field.field) ? '' : 'display: none'"
                    >
                        <option
                                v-for="format in dateFormats">{{format}}
                        </option>
                    </select></td>

                    <td class="subitem-actions">
                        <button @click="deleteField(index)" class="subitem-delete-button"></button>
                        <button @click="fields.splice(index + 1, 0, Object.assign({}, emptyField))" class="subitem-add-button"></button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <div class="popup-actions">
            <button
                    class="cancel-button"
                    @click="closePopup()"
            >{{t('cancel')}}</button>
            <button
                    :disabled="!readyToSave"
                    class="save-button"
                    @click="saveClicked()"
            >{{t('save')}}</button>
        </div>
    </div>
</template>

<script>

    import axios from 'axios';
    import CustomFields from '../../../../../shared_utils/CustomFields.js';
    import App from '../../../../App.js';

    // Only for testing, we don't have the appMain bootstrap
    import AlertUI from "../../../../../appHelpers/Alert";
    import UrlBuilder from "../../../../../appHelpers/UrlBuilder";
    import i18n from '../../../../i18n/i18n';

    if (!App.AlertUI) {
        App.AlertUI = new AlertUI(i18n);
    }

    if (!App.apiUrl) {
        const apiRoot = '/api/';

        App.apiUrl = function (path, options, timestamp) {
            // Remove extra / at beginning of path, if present
            if (path.charAt(0) === '/') {
                path = path.substr(1);
            }

            return UrlBuilder.buildUrl(apiRoot + path, options, timestamp);
        };
    }

    const DATE_FORMATS = [
        'dd/MM/yyyy'
        , 'dd.MM.yyyy'
        , 'dd-MM-yyyy'
        , 'MM/dd/yyyy'
        , 'MM.dd.yyyy'
        , 'MM-dd-yyyy'
        , 'yyyy-MM-dd'
        , 'yyyyMMdd'
    ];

    const TemplateField = function (label, sampleValue, field, format) {
        this.label = label;
        this.sampleValue = sampleValue;
        this.field = field;
        this.format = format;
    };

    export default {
        name: "EditImportTemplate",
        props: ['value', 'templateId', 'originalTemplateData', 'projectId', 'fieldsList', 'dateFields', 'type'],
        data() {
            return {
                selectedFile: '',
                templateName: '',
                emptyField: new TemplateField(),
                loadingFile: false,
                dateFormats: DATE_FORMATS,
                fields: [new TemplateField()],
            }
        },
        created() {
            if (!this.originalTemplateData) return;

            this.templateName = this.originalTemplateData.name;

            const {columns: templateData} = JSON.parse(this.originalTemplateData.template);

            this.fields = templateData.map(field =>
                new TemplateField(field.label, field.sample_value, field.name, field.format));
        },
        computed: {
            selectedFileName() {
                return this.selectedFile && this.selectedFile.name
            },
            readyToSave() {
                return !!this.templateName
            },
        },
        methods: {
            async saveClicked() {

                const columns = this.fields.map(field => ({
                    label: field.label,
                    name: field.field,
                    sample_value: field.sampleValue,
                    format: field.format,
                }));

                let assignedProject;

                if (!this.projectId) {
                    const {data: {id: projectId}} = await axios.get(App.apiUrl('me/assigned_project'));
                    assignedProject = projectId;
                }

                const body = {
                    name: this.templateName,
                    type: this.type,
                    project_id: this.projectId || assignedProject,
                    template: JSON.stringify({columns}),
                };

                App.AlertUI.showLoader();

                try {

                    const apiUrl = this.templateId
                        ? App.apiUrl(`import_templates/${this.templateId}`)
                        : App.apiUrl('import_templates');

                    const {data: result} = await axios.post(apiUrl, body);

                    this.$emit('saveClicked', result.id);

                    this.closePopup();

                } catch(err) {
                    App.showXhrError(err.request, err);
                }

                App.AlertUI.hideLoader();

            },
            closePopup() {

                this.$emit('input', 0);

            },
            async fileChange(event) {

                this.selectedFile = event.target.files[0];

                if (!this.selectedFile) return;

                if (!this.templateName) {
                    this.templateName = this.selectedFileName.split('.csv')[0];
                }

                this.loadingFile = true;

                const formData = new FormData();

                formData.append('csv', this.selectedFile);

                try {

                    const {data} = await axios
                        .post(App.apiUrl('csv/header_and_first_line'), formData);

                    const [[headers, firstRow]] = data;

                    this.updateTemplateData(headers, firstRow)

                } catch (err) {
                    console.log(err);
                }

                this.loadingFile = false;

            },
            updateTemplateData(headers, firstRow) {

                return this.fields = headers.map((header, index) => {

                    const sampleValue = firstRow[index];

                    const {value: mapField} = this.findMatch(header);

                    return new TemplateField(header, sampleValue, mapField);

                });

            },
            deleteField(index) {
                if (this.fields.length < 2) {
                    return this.fields = [new TemplateField()];
                }
                this.fields.splice(index, 1);
            },
            findMatch(label) {

                // TODO: Smarter matches...
                return this.fieldsList.find(field =>
                    field.text === label || field.text.indexOf(label) > -1) || {};

            }
        }
    }
</script>
